/**
 * OperationProgressBar Component
 *
 * An enhanced progress bar that shows success (green) vs error (red) portions
 * for bulk operations. Provides visual feedback on partial success scenarios.
 */

'use client';

import React from 'react';
import { cn } from '@/lib/utils';

// =============================================================================
// TYPES
// =============================================================================

export interface OperationProgressBarProps {
  /** Total number of items in the operation */
  total: number;
  /** Number of items processed (success + error) */
  processed: number;
  /** Number of items that succeeded */
  successCount: number;
  /** Number of items that failed */
  errorCount: number;
  /** Height of the progress bar */
  size?: 'sm' | 'md' | 'lg';
  /** Show percentage label */
  showPercentage?: boolean;
  /** Show counts label (e.g., "26 of 28") */
  showCounts?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether operation is indeterminate (no progress info yet) */
  indeterminate?: boolean;
}

// =============================================================================
// STYLES
// =============================================================================

const sizeStyles = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

// =============================================================================
// COMPONENT
// =============================================================================

export function OperationProgressBar({
  total,
  processed,
  successCount,
  errorCount,
  size = 'md',
  showPercentage = false,
  showCounts = false,
  className,
  indeterminate = false,
}: OperationProgressBarProps) {
  // Calculate percentages
  const totalPercentage = total > 0 ? Math.round((processed / total) * 100) : 0;
  const successPercentage = total > 0 ? Math.round((successCount / total) * 100) : 0;
  const errorPercentage = total > 0 ? Math.round((errorCount / total) * 100) : 0;

  // Determine the pending/remaining portion
  const pendingPercentage = 100 - successPercentage - errorPercentage;

  return (
    <div className={cn('w-full', className)}>
      {/* Progress bar container */}
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-full bg-[var(--surface-skeleton)]',
          sizeStyles[size]
        )}
        role="progressbar"
        aria-valuenow={totalPercentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Operation progress: ${processed} of ${total} items processed`}
      >
        {indeterminate ? (
          // Indeterminate animation
          <div
            className={cn(
              'absolute inset-0 bg-blue-500',
              'animate-pulse'
            )}
          />
        ) : (
          // Stacked progress bars for success/error split
          <div className="flex h-full">
            {/* Success portion (green) */}
            {successPercentage > 0 && (
              <div
                className="h-full bg-green-500 transition-all duration-300 ease-in-out"
                style={{ width: `${successPercentage}%` }}
              />
            )}
            {/* Error portion (red) */}
            {errorPercentage > 0 && (
              <div
                className="h-full bg-red-500 transition-all duration-300 ease-in-out"
                style={{ width: `${errorPercentage}%` }}
              />
            )}
            {/* Pending portion (shows progress during operation) */}
            {pendingPercentage < 100 && pendingPercentage > 0 && (
              <div
                className="h-full bg-[var(--surface-skeleton)] transition-all duration-300 ease-in-out"
                style={{ width: `${pendingPercentage}%` }}
              />
            )}
          </div>
        )}
      </div>

      {/* Labels */}
      {(showPercentage || showCounts) && (
        <div className="mt-1 flex justify-between text-xs text-[var(--text-secondary)]">
          {showCounts && (
            <span>
              {processed} of {total} items
              {errorCount > 0 && (
                <span className="text-red-600 ml-1">
                  ({errorCount} failed)
                </span>
              )}
            </span>
          )}
          {showPercentage && (
            <span className={cn(!showCounts && 'ml-auto')}>
              {totalPercentage}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// SIMPLE PROGRESS BAR (for cases without success/error split)
// =============================================================================

export interface SimpleProgressBarProps {
  /** Progress value (0-100) */
  value: number;
  /** Height of the progress bar */
  size?: 'sm' | 'md' | 'lg';
  /** Color variant */
  variant?: 'default' | 'success' | 'warning' | 'error';
  /** Show percentage label */
  showPercentage?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether progress is indeterminate */
  indeterminate?: boolean;
}

const variantStyles = {
  default: 'bg-blue-500',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  error: 'bg-red-500',
};

export function SimpleProgressBar({
  value,
  size = 'md',
  variant = 'default',
  showPercentage = false,
  className,
  indeterminate = false,
}: SimpleProgressBarProps) {
  const clampedValue = Math.min(100, Math.max(0, value));

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-full bg-[var(--surface-skeleton)]',
          sizeStyles[size]
        )}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        {indeterminate ? (
          <div
            className={cn(
              'absolute inset-0',
              variantStyles[variant],
              'animate-pulse'
            )}
          />
        ) : (
          <div
            className={cn(
              'h-full transition-all duration-300 ease-in-out',
              variantStyles[variant]
            )}
            style={{ width: `${clampedValue}%` }}
          />
        )}
      </div>

      {showPercentage && (
        <div className="mt-1 text-right text-xs text-[var(--text-secondary)]">
          {Math.round(clampedValue)}%
        </div>
      )}
    </div>
  );
}

export default OperationProgressBar;
